import React, { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { currentRobot } from './store/DataStore';
import EditableItemList from './EditableItemList';

const defaultFetchItems = [];
const defaultDeliverItems = [];

const ActionDefaultsSetting = () => {
  const [savedRobot] = useAtom(currentRobot);
  if (!savedRobot) {
    return <p className='font-heebo font-light'>Select a robot to edit its default items.</p>;
  }
  return <RobotActionDefaults key={savedRobot} savedRobot={savedRobot} />;
};

const RobotActionDefaults = ({ savedRobot }) => {
  const [fetchItems, setFetchItems] = useState(defaultFetchItems);
  const [deliverItems, setDeliverItems] = useState(defaultDeliverItems);
  const [isSavedForm, setIsSavedForm] = useState(true);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;

    async function getActionDefaults() {
      const docRef = doc(db, `Companies/Zeeno_Dev/Settings/${savedRobot}/Actions/actionssettings`);

      try {
        const docSnapshot = await getDoc(docRef);

        if (cancelled) return;

        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          setFetchItems(Array.isArray(data.fetch_default_items) ? data.fetch_default_items : defaultFetchItems);
          setDeliverItems(Array.isArray(data.deliver_default_items) ? data.deliver_default_items : defaultDeliverItems);
        } else {
          setFetchItems(defaultFetchItems);
          setDeliverItems(defaultDeliverItems);
        }

        setIsSavedForm(true);
        setIsLoading(false);
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching action defaults: ", error);
        setError('Unable to load default items. Leave settings and return to try again.');
      }
    }

    getActionDefaults();
    return () => {
      cancelled = true;
      mounted.current = false;
    };
  }, [savedRobot]);

  const onSavePressed = async () => {
    if (isLoading || isSaving || isSavedForm) return;
    setIsSaving(true);
    setError('');
    const docRef = doc(db, `Companies/Zeeno_Dev/Settings/${savedRobot}/Actions/actionssettings`);

    try {
      await setDoc(docRef, {
        fetch_default_items: fetchItems,
        deliver_default_items: deliverItems,
      }, { merge: true });
      if (mounted.current) setIsSavedForm(true);
    } catch (error) {
      console.error('Error saving action defaults: ', error);
      if (mounted.current) setError('Unable to save default items. Please try again.');
    } finally {
      if (mounted.current) setIsSaving(false);
    }
  }

  return (
    <div className='flex flex-col'>
      {error && <p role='alert' className='text-red-600'>{error}</p>}
      {isLoading && !error && <p role='status'>Loading default items…</p>}
      <fieldset disabled={isLoading || isSaving} className='min-w-0'>
        <h1 className='text-lg font-heebo'>Fetch Default Items</h1>
        <span className='font-heebo font-light inline-block max-w-5xl'>Items shown as suggestions in the Fetch popup. Users can select one or more, or type a custom item for one task.</span>
        <EditableItemList
          items={fetchItems}
          onChange={(items) => { setFetchItems(items); setIsSavedForm(false); }}
          placeholder='Add fetch item'
        />

        <hr className='mt-4 opacity-60' />
        <h1 className='text-lg font-heebo mt-4'>Deliver Default Items</h1>
        <span className='font-heebo font-light inline-block max-w-5xl'>Items shown as suggestions in the Deliver popup. Users can select one or more, or type a custom item for one task.</span>
        <EditableItemList
          items={deliverItems}
          onChange={(items) => { setDeliverItems(items); setIsSavedForm(false); }}
          placeholder='Add deliver item'
        />

        <button
          onClick={onSavePressed}
          disabled={isLoading || isSaving || isSavedForm}
          className={`px-5 py-2 ${isLoading || isSaving || isSavedForm ? 'bg-opacity-40' : 'hover:bg-opacity-100 bg-opacity-80 cursor-pointer'} bg-[#3C5BD2] text-white font-heebo w-fit rounded-lg mt-3`}
        >
          {isSaving ? 'Saving…' : isLoading ? 'Save' : isSavedForm ? 'Saved' : 'Save'}
        </button>
      </fieldset>
    </div>
  )
}

export default ActionDefaultsSetting;
